import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  alpha,
  useTheme,
  Divider,
  Alert,
} from "@mui/material";
import moment from "moment";
import { VoucherSettings } from "../../../mock.data";
import { useConfiguration } from "../../../components/ConfigurationContext";

export const VoucherSettingsConfig: React.FC = () => {
  const theme = useTheme();
  const { voucherSettings, updateVoucherSettings } = useConfiguration();
  const [settings, setSettings] = useState<VoucherSettings>(voucherSettings);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState("");
  const [selectedTemplateType, setSelectedTemplateType] = useState<
    "payment" | "receipt" | "journal" | "contra" | "opening"
  >("payment");

  // Section 1: Enabled Voucher Types handlers
  const handleVoucherTypeToggle = (
    type: keyof VoucherSettings["enabled_voucher_types"],
    value: boolean
  ) => {
    setSettings((prev) => ({
      ...prev,
      enabled_voucher_types: {
        ...prev.enabled_voucher_types,
        [type]: value,
      },
      updated_at: moment().valueOf(),
    }));
  };

  // Section 2: Voucher Numbering Format handlers
  const handlePrefixChange = (
    type: keyof VoucherSettings["voucher_type_prefixes"],
    value: string
  ) => {
    setSettings((prev) => ({
      ...prev,
      voucher_type_prefixes: {
        ...prev.voucher_type_prefixes,
        [type]: value,
      },
      updated_at: moment().valueOf(),
    }));
  };

  const handleSequencePerBranchToggle = (value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      separate_sequence_per_branch: value,
      updated_at: moment().valueOf(),
    }));
  };

  // Section 3: Payment Methods handlers
  const handlePaymentMethodToggle = (
    method: keyof VoucherSettings["payment_method_settings"],
    value: boolean
  ) => {
    setSettings((prev) => ({
      ...prev,
      payment_method_settings: {
        ...prev.payment_method_settings,
        [method]: value,
      },
      updated_at: moment().valueOf(),
    }));
  };

  const handleDefaultPaymentMethodChange = (value: string) => {
    setSettings((prev) => ({
      ...prev,
      payment_method_settings: {
        ...prev.payment_method_settings,
        default_payment_method: value,
      },
      updated_at: moment().valueOf(),
    }));
  };

  // Section 4: Field Requirements handlers
  const handleFieldRequirementToggle = (
    field: keyof VoucherSettings["field_requirements"],
    value: boolean
  ) => {
    setSettings((prev) => ({
      ...prev,
      field_requirements: {
        ...prev.field_requirements,
        [field]: value,
      },
      updated_at: moment().valueOf(),
    }));
  };

  const handleAttachmentRequiredChange = (
    type: keyof VoucherSettings["attachment_required"],
    value: boolean
  ) => {
    setSettings((prev) => ({
      ...prev,
      attachment_required: {
        ...prev.attachment_required,
        [type]: value,
      },
      updated_at: moment().valueOf(),
    }));
  };

  // Section 5: Templates handlers
  const handleAddTemplate = () => {
    if (newTemplate.trim()) {
      setSettings((prev) => ({
        ...prev,
        default_narration_templates: {
          ...prev.default_narration_templates,
          [selectedTemplateType]: [
            ...prev.default_narration_templates[selectedTemplateType],
            newTemplate.trim(),
          ],
        },
        updated_at: moment().valueOf(),
      }));
      setNewTemplate("");
      setIsTemplateDialogOpen(false);
    }
  };

  const handleRemoveTemplate = (
    type: keyof VoucherSettings["default_narration_templates"],
    template: string
  ) => {
    setSettings((prev) => ({
      ...prev,
      default_narration_templates: {
        ...prev.default_narration_templates,
        [type]: prev.default_narration_templates[type].filter(
          (t) => t !== template
        ),
      },
      updated_at: moment().valueOf(),
    }));
  };

  // Save settings function
  const handleSaveSettings = () => {
    console.log("Saving voucher settings:", settings);
    updateVoucherSettings(settings);
    // Here you would typically save to backend
  };

  const voucherTypes = [
    {
      key: "payment",
      label: "Payment Vouchers",
      description: "Payment to vendor, supplier, refund, advance",
    },
    {
      key: "receipt",
      label: "Receipt Vouchers",
      description: "Receipts from customer, deposits, advances",
    },
    {
      key: "journal",
      label: "Journal Vouchers",
      description: "Internal transfers, corrections, reclassifications",
    },
    {
      key: "contra",
      label: "Contra Vouchers",
      description: "For cash-to-bank or bank-to-bank transfers",
    },
    {
      key: "opening",
      label: "Opening Vouchers",
      description: "Used during system setup to initialize balances",
    },
  ];

  const paymentMethods = [
    {
      key: "enable_cash",
      label: "Cash",
      description: "Show 'Cash' as a payment option",
    },
    {
      key: "enable_bank_transfer",
      label: "Bank Transfer",
      description: "Used for WPS, vendor payments",
    },
    {
      key: "enable_qr_payment",
      label: "QR Payment",
      description: "BenefitPay, VPay, etc.",
    },
    {
      key: "enable_cheque",
      label: "Cheque",
      description: "Optional for regions using cheques",
    },
    {
      key: "enable_split_payments",
      label: "Split Payments",
      description: "Allow combining methods (e.g., Cash + QR)",
    },
  ];

  const fieldRequirements = [
    {
      key: "require_narration",
      label: "Require Narration",
      description: "Prevent submission without narration",
    },
    {
      key: "require_reference_linking",
      label: "Require Reference Linking",
      description: "Enables 'Linked Invoice/PO' field",
    },
    {
      key: "require_attachment_upload",
      label: "Require Attachment Upload",
      description: "Blocks posting without document",
    },
    {
      key: "allow_line_level_narration",
      label: "Allow Line-level Narration",
      description: "Enables narration per COA line",
    },
    {
      key: "enable_multiline_vouchers",
      label: "Enable Multi-line Vouchers",
      description: "Allows user to split amounts across multiple COA lines",
    },
    {
      key: "enable_voucher_draft_mode",
      label: "Enable Voucher Draft Mode",
      description: "Allows saving without posting (for approval later)",
    },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 0.5,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
          >
            Voucher Settings Configuration
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", fontSize: "1rem" }}
          >
            Configure voucher types, numbering, payment methods, and validation
            rules
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleSaveSettings}
          startIcon={<Icon>save</Icon>}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1.5,
          }}
        >
          Save Settings
        </Button>
      </Box>

      <Box sx={{ flex: 1, p: 3 }}>
        <Grid container spacing={4}>
          {/* Section 1: Enabled Voucher Types */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "primary.main" }}>toggle_on</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 1: Enabled Voucher Types
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      This governs which voucher types are available across the
                      system
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {voucherTypes.map((type) => (
                    <Grid item xs={12} sm={6} md={4} key={type.key}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  settings.enabled_voucher_types[
                                    type.key as keyof typeof settings.enabled_voucher_types
                                  ]
                                }
                                onChange={(e) =>
                                  handleVoucherTypeToggle(
                                    type.key as keyof typeof settings.enabled_voucher_types,
                                    e.target.checked
                                  )
                                }
                                color="primary"
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {type.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {type.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 2: Voucher Numbering Format */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "secondary.main" }}>
                      format_list_numbered
                    </Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 2: Voucher Numbering Format
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Defines the voucher ID generation rules for each type
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {voucherTypes.map((type) => (
                    <Grid item xs={12} sm={6} md={2.4} key={type.key}>
                      <TextField
                        fullWidth
                        label={`${type.label.split(" ")[0]} Prefix`}
                        value={
                          settings.voucher_type_prefixes[
                            type.key as keyof typeof settings.voucher_type_prefixes
                          ]
                        }
                        onChange={(e) =>
                          handlePrefixChange(
                            type.key as keyof typeof settings.voucher_type_prefixes,
                            e.target.value
                          )
                        }
                        placeholder={`${type.key.toUpperCase().slice(0, 2)}-`}
                        helperText={`e.g., ${
                          settings.voucher_type_prefixes[
                            type.key as keyof typeof settings.voucher_type_prefixes
                          ]
                        }2025-0001`}
                        disabled={
                          !settings.enabled_voucher_types[
                            type.key as keyof typeof settings.enabled_voucher_types
                          ]
                        }
                      />
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    ID Format Rule:
                  </Typography>
                  <Typography variant="body2">
                    Prefix + Year + Sequence → e.g., PV-2025-000031
                  </Typography>
                </Alert>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.separate_sequence_per_branch}
                      onChange={(e) =>
                        handleSequencePerBranchToggle(e.target.checked)
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Separate Sequence Per Branch
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        If enabled, voucher ID resets per branch
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Section 3: Allowed Payment Methods */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "success.main" }}>payment</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 3: Allowed Payment Methods
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Controls what payment modes are accepted in voucher
                      templates
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {paymentMethods.map((method) => (
                    <Grid item xs={12} sm={6} md={4} key={method.key}>
                      <Card variant="outlined">
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  settings.payment_method_settings[
                                    method.key as keyof typeof settings.payment_method_settings
                                  ] as boolean
                                }
                                onChange={(e) =>
                                  handlePaymentMethodToggle(
                                    method.key as keyof typeof settings.payment_method_settings,
                                    e.target.checked
                                  )
                                }
                                color="success"
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {method.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {method.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Default Payment Method</InputLabel>
                      <Select
                        value={
                          settings.payment_method_settings
                            .default_payment_method
                        }
                        label="Default Payment Method"
                        onChange={(e) =>
                          handleDefaultPaymentMethodChange(e.target.value)
                        }
                      >
                        {settings.allowed_payment_methods.map((method) => (
                          <MenuItem key={method} value={method}>
                            {method}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 4: Field Requirements & Behavior */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "warning.main" }}>rule</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 4: Field Requirements & Behavior
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      These are validation and behavior toggles applied to
                      voucher entry
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {fieldRequirements.map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field.key}>
                      <Card variant="outlined">
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  settings.field_requirements[
                                    field.key as keyof typeof settings.field_requirements
                                  ]
                                }
                                onChange={(e) =>
                                  handleFieldRequirementToggle(
                                    field.key as keyof typeof settings.field_requirements,
                                    e.target.checked
                                  )
                                }
                                color="warning"
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {field.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {field.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Attachment Requirements by Voucher Type
                </Typography>
                <Grid container spacing={2}>
                  {voucherTypes.map((type) => (
                    <Grid item xs={12} sm={6} md={2.4} key={type.key}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              settings.attachment_required[
                                type.key as keyof typeof settings.attachment_required
                              ]
                            }
                            onChange={(e) =>
                              handleAttachmentRequiredChange(
                                type.key as keyof typeof settings.attachment_required,
                                e.target.checked
                              )
                            }
                            disabled={
                              !settings.enabled_voucher_types[
                                type.key as keyof typeof settings.enabled_voucher_types
                              ]
                            }
                          />
                        }
                        label={type.label.split(" ")[0]}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 5: Default Narration Templates */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: "info.main" }}>description</Icon>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Section 5: Default Narration Templates
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        Allows predefined narration formats per voucher type,
                        using placeholders
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<Icon>add</Icon>}
                    onClick={() => setIsTemplateDialogOpen(true)}
                  >
                    Add Template
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Use <strong>{`{{variable_name}}`}</strong> for dynamic
                    placeholders. Examples: {`{{vendor}}`}, {`{{invoice_no}}`},{" "}
                    {`{{payment_method}}`}, {`{{account_name}}`}
                  </Typography>
                </Alert>

                <Grid container spacing={3}>
                  {voucherTypes
                    .filter(
                      (type) =>
                        settings.enabled_voucher_types[
                          type.key as keyof typeof settings.enabled_voucher_types
                        ]
                    )
                    .map((type) => (
                      <Grid item xs={12} md={6} lg={4} key={type.key}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            textTransform: "capitalize",
                            color: "primary.main",
                          }}
                        >
                          {type.label} Templates
                        </Typography>
                        <List dense>
                          {settings.default_narration_templates[
                            type.key as keyof typeof settings.default_narration_templates
                          ].map((template, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                bgcolor: alpha(
                                  theme.palette.background.default,
                                  0.5
                                ),
                                borderRadius: 1,
                                mb: 1,
                                border: `1px solid ${alpha(
                                  theme.palette.divider,
                                  0.1
                                )}`,
                              }}
                            >
                              <ListItemText
                                primary={template}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  sx: {
                                    fontFamily: "monospace",
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleRemoveTemplate(
                                      type.key as keyof typeof settings.default_narration_templates,
                                      template
                                    )
                                  }
                                  sx={{ color: "error.main" }}
                                >
                                  <Icon>delete</Icon>
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                          {settings.default_narration_templates[
                            type.key as keyof typeof settings.default_narration_templates
                          ].length === 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontStyle: "italic",
                              }}
                            >
                              No templates configured
                            </Typography>
                          )}
                        </List>
                      </Grid>
                    ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Add Template Dialog */}
      <Dialog
        open={isTemplateDialogOpen}
        onClose={() => setIsTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Icon sx={{ color: "info.main" }}>description</Icon>
            Add Narration Template
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Voucher Type</InputLabel>
            <Select
              value={selectedTemplateType}
              label="Voucher Type"
              onChange={(e) =>
                setSelectedTemplateType(
                  e.target.value as typeof selectedTemplateType
                )
              }
            >
              {voucherTypes
                .filter(
                  (type) =>
                    settings.enabled_voucher_types[
                      type.key as keyof typeof settings.enabled_voucher_types
                    ]
                )
                .map((type) => (
                  <MenuItem key={type.key} value={type.key}>
                    {type.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Template Text"
            value={newTemplate}
            onChange={(e) => setNewTemplate(e.target.value)}
            placeholder="e.g., Payment to {{vendor}} for invoice {{invoice_no}}"
            multiline
            rows={3}
            helperText="Use {{variable_name}} for dynamic placeholders"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Common placeholders:
            </Typography>
            <Typography variant="body2">
              {`{{vendor}}, {{customer}}, {{invoice_no}}, {{order_number}}, {{payment_method}}, {{account_name}}, {{date}}, {{period}}, {{location}}, {{purpose}}, {{reason}}`}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddTemplate}
            disabled={!newTemplate.trim()}
            startIcon={<Icon>add</Icon>}
          >
            Add Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
